import React, { useEffect } from "react";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import AdminLayout from "layouts/Admin.js";
import ManagerLayout from "layouts/Manager.js";
import AuthLayout from "layouts/Auth.js";
import { useSelector, useDispatch } from "react-redux";
import TermsAndConditions from "views/examples/TaC";
import ScanBadges from "views/examples/ScanBadges";
import VerifySeasonalBadges from "views/examples/VerifySeasonalBadges";
import EmailVerificationPending from "views/auth/emailVerification/EmailVerificationPending";
import firebase from "./config/firebase";
import { loginSuccessFull } from "./store/actions/authActions";
import { isLoggingOut, clearLoggingOut } from "./store/actions/authLogoutFlag";

const TermsAndConditionsPage = () => {
  return <TermsAndConditions />;
};

function App() {
  let { uid, isManager, needsVerification } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const urlParams = new URLSearchParams(window.location.search);
  let redirectPath = urlParams.get("redirect");
  
  if (redirectPath && (redirectPath.startsWith("http") || redirectPath.startsWith("//"))) {
    redirectPath = null;
  }

  // Initialize Microsoft Clarity only in production
  useEffect(() => {
    if (process.env.REACT_APP_ENABLE_CLARITY === "true") {
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "xm7h6qp1n5");
    }
  }, []);

  // Single auth state listener, mounted once.
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (firebaseUser) => {
      // If logout() was just called, skip this callback — it fires before
      // Firebase signOut() settles but Redux is already cleared.
      if (isLoggingOut()) {
        clearLoggingOut();
        return;
      }

      if (!firebaseUser) {
        return;
      }

      // Don't interfere during the registration flow.
      if (window.location.pathname.includes('/auth/register')) {
        return;
      }

      try {
        await firebaseUser.reload();
      } catch (_) {
        return;
      }
      const freshUser = firebase.auth().currentUser;
      if (!freshUser) return;

      if (freshUser.emailVerified) {
        dispatch({ type: "CLEAR_VERIFICATION_STATE" });
        dispatch(loginSuccessFull(freshUser.uid));
      } else {
        try {
          const userDoc = await firebase
            .firestore()
            .collection("users")
            .doc(freshUser.uid)
            .get();
          if (userDoc.exists) {
            dispatch({
              type: "SET_UNVERIFIED_USER",
              payload: {
                uid: freshUser.uid,
                email: freshUser.email,
                needsVerification: true,
              },
            });
          }
        } catch (error) {
          console.error("Error checking user document:", error);
        }
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  return (
    <>
      <BrowserRouter>
        {/* If user needs email verification, show verification page */}
        {needsVerification ? (
          <Switch>
            <Route
              exact
              path="/auth/email-verification"
              component={EmailVerificationPending}
            />
            <Route
              exact
              path="/terms-and-conditions"
              component={TermsAndConditionsPage}
            />
            <Redirect to="/auth/email-verification" />
          </Switch>
        ) : uid ? (
          /* User is authenticated and verified */
          <>
            {isManager ? (
              <Switch>
                <Route
                  path="/manager"
                  render={(props) => <ManagerLayout {...props} />}
                />
                {redirectPath ? (
                  <Redirect from="/" to={redirectPath} />
                ) : (
                  <Redirect from="/" to="/manager/dashboard" />
                )}
              </Switch>
            ) : (
              <Switch>
                <Route
                  path="/fan"
                  render={(props) => <AdminLayout {...props} />}
                />
                <Route
                  exact
                  path="/terms-and-conditions"
                  component={TermsAndConditionsPage}
                />
                {redirectPath ? (
                  <Redirect from="/" to={`/fan/${redirectPath}`} />
                ) : (
                  <Redirect from="/" to="/fan/manager-training" />
                )}
              </Switch>
            )}
          </>
        ) : (
          /* User is not authenticated */
          <Switch>
            <Route path="/auth" render={(props) => <AuthLayout {...props} />} />
            <Route
              exact
              path="/terms-and-conditions"
              component={TermsAndConditionsPage}
            />
            <Redirect from="/" to="/auth/login" />
          </Switch>
        )}
        {/* Public routes accessible regardless of auth state */}
        <Route path="/verifyLicense/:badgeID" exact component={ScanBadges} />
        <Route
          path="/verify/talking_badges/:id"
          exact
          component={VerifySeasonalBadges}
        />
      </BrowserRouter>
    </>
  );
}

export default App;
